import type { AxiosInstance } from "axios";
import axios from "axios";
import { invalidateProductCacheOnCitizenDataMutation } from "../../../lib/client-api/interceptors/product-cache-invalidation/product-cache-invalidation.interceptor";
import type { IApiClientRequestConfigOptions } from "../ApiClient/ApiClientRequestConfigOptions.interface";
import type { IApiClientService } from "../ApiClient/ApiClientService.interface";
import axiosInstance from "../axios-config-singleton/axios-config-singleton.instance";
import type { IApiClientConfig } from "./dtos/AxiosApiClientConfig";
import type { IApiClientResponse } from "./dtos/AxiosApiClientResponse";
import { AxiosApiClientResponse } from "./dtos/AxiosApiClientResponse";

class AxiosApiClientInstance implements IApiClientService {
  private readonly _clientInstance: AxiosInstance;

  constructor(config?: IApiClientConfig) {
    this._clientInstance = axios.create({
      ...axiosInstance.defaults,
      ...config,
    });
    this._clientInstance.interceptors.response.use(invalidateProductCacheOnCitizenDataMutation);
  }

  public async get<Data>(url: string, options: IApiClientRequestConfigOptions): Promise<IApiClientResponse<Data>> {
    const responseFromAxios = await this._clientInstance.get<Data>(url, options);
    const response = new AxiosApiClientResponse<Data>(responseFromAxios);

    return response;
  }

  public async post<Data, ResponseData>(
    url: string,
    body: Data,
    options: IApiClientRequestConfigOptions
  ): Promise<IApiClientResponse<ResponseData>> {
    const responseFromAxios = await this._clientInstance.post<ResponseData>(url, body, options);
    const response = new AxiosApiClientResponse<ResponseData>(responseFromAxios);

    return response;
  }

  public async patch<Data, ResponseData>(
    url: string,
    body: Data,
    options: IApiClientRequestConfigOptions
  ): Promise<IApiClientResponse<ResponseData>> {
    const responseFromAxios = await this._clientInstance.patch<ResponseData>(url, body, options);
    const response = new AxiosApiClientResponse<ResponseData>(responseFromAxios);

    return response;
  }

  public async delete(url: string, options: IApiClientRequestConfigOptions): Promise<IApiClientResponse<void>> {
    const responseFromAxios = await this._clientInstance.delete(url, options);
    const response = new AxiosApiClientResponse<void>(responseFromAxios);

    return response;
  }
}

export default AxiosApiClientInstance;
